import React from 'react'
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SelectClub from '@/components/features/clubs/select-club';
import SelectPlayer from '@/components/features/players/select-player';
import SelectPosition from '@/components/features/players/select-position';
import SelectRolePosition from '@/components/features/players/select-role-position';
import { Textarea } from '@/components/ui/textarea';
import MultiSelect from '@/components/ui/multi-select';
import { LANGUAGES } from '@/constants/languages';
import { Input } from '@/components/ui/input';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { useTRPC } from '@/trpc';
import { TRPCClientError } from '@trpc/client';
import { TContactPlayerCreate } from '@/types/trpc.types';
import * as Icon from 'lucide-react'

interface Props {
    contact_id: string;
    trigger?: React.ReactNode;
    onCreate?: (player:TContactPlayerCreate) => void;
}

const createPlayersOfferedSchema = z.object({
    offered_by_id: z.string().uuid(),
    mongo_player_id: z.string().min(1),
    mongo_club_id: z.string().optional(),
    external_url: z.string().optional(),
    position: z.string().optional(),
    role_position: z.string().optional(),
    note: z.string().optional(),
    languages: z.array(z.string()).optional(),
    mandate: z.string().optional(),
    telephone: z.string().optional(),
    email: z.string().email().optional(),
    transfer_amount: z.number().optional(),
    salary_expectation: z.number().optional(),
    extra_info: z.string().optional(),
    company: z.string().optional(),
})
type CreatePlayersOfferedSchema = z.infer<typeof createPlayersOfferedSchema>

const CreatePlayersOffered = ({ contact_id, trigger, onCreate }: Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);

    const form = useForm<CreatePlayersOfferedSchema>({
        defaultValues: {
            offered_by_id: contact_id
        }
    })
    const club_id = form.watch("mongo_club_id")

    const createMutation = useMutation({
        mutationFn: async (data:CreatePlayersOfferedSchema) => {
            const result = await trpc.contacts.playersOffered.create.mutate(data);
            return result as TContactPlayerCreate
        },
        onSuccess: (result) => {
            toast.success("Speler succesvol toegevoegd.")
            setOpen(false);
            onCreate?.(result)
            form.reset();
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    const handleInputFileChange = (e:React.ChangeEvent<HTMLInputElement>, key:keyof CreatePlayersOfferedSchema) => {
        const file = e.target.files?.[0];
        if(file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                form.setValue(key, reader.result as string);
            };
        }
    }

    const handleSubmit = () => {
        const values = form.getValues();
        if(!createPlayersOfferedSchema.safeParse(values).success) {
            toast.error("Vul alle velden correct in.");
            return;
        }
        createMutation.mutate(values);
    }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent className='gap-0'>
            <SheetHeader>
                <SheetTitle>Nieuwe aangeboden speler</SheetTitle>
                <SheetDescription>Vul de gewenste gegevens in</SheetDescription>
            </SheetHeader>
            <div className='h-full overflow-auto p-4'>
                <div className='flex flex-col gap-4'>
                    <div>
                        <Label>Club</Label>
                        <Controller 
                            control={form.control}
                            name="mongo_club_id"
                            render={({field})=>(
                                <SelectClub 
                                    value={field.value} 
                                    onValueChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label required>Speler</Label>
                        <Controller 
                            control={form.control}
                            name="mongo_player_id"
                            render={({field})=>(
                                <SelectPlayer 
                                    value={field.value} 
                                    onValueChange={field.onChange}
                                    searchOptions={{
                                        competition: {
                                            club_id: club_id
                                        }
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className='flex items-center gap-4'>
                        <div>
                            <Label>Positie</Label>
                            <Controller 
                                control={form.control}
                                name="position"
                                render={({field})=>(
                                    <SelectPosition 
                                        selectedValue={field.value} 
                                        onValueChange={field.onChange} 
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <Label>Rol in positie</Label>
                            <Controller 
                                control={form.control}
                                name="role_position"
                                render={({field})=>(
                                    <SelectRolePosition 
                                        selectedValue={field.value} 
                                        onValueChange={field.onChange} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Externe url</Label>
                        <Controller
                            control={form.control}
                            name="external_url"
                            render={({field})=>(
                                <Input 
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Notitie</Label>
                        <Controller
                            control={form.control}
                            name="note"
                            render={({field})=>(
                                <Textarea 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Talen</Label>
                        <Controller
                            control={form.control}
                            name="languages"
                            render={({field})=>(
                                <MultiSelect 
                                    selectedOptions={field.value ?? []} 
                                    onValueChange={field.onChange} 
                                    options={LANGUAGES}
                                    placeholder='Selecteer talen'
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Mandaat</Label>
                        <Controller
                            control={form.control}
                            name="mandate"
                            render={({field})=>(
                                <Input 
                                    type="file"
                                    onChange={(e)=>{handleInputFileChange(e, 'mandate')}} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Voor bedrijf</Label>
                        <Controller
                            control={form.control}
                            name="company"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Contact telefoon</Label>
                        <Controller
                            control={form.control}
                            name="telephone"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Contact e-mail</Label>
                        <Controller
                            control={form.control}
                            name="email"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Transferbedrag</Label>
                        <Controller
                            control={form.control}
                            name="transfer_amount"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={(e)=>field.onChange(e.target.valueAsNumber)} 
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Loonsverwachting</Label>
                        <Controller
                            control={form.control}
                            name="salary_expectation"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={(e)=>field.onChange(e.target.valueAsNumber)} 
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Extra info</Label>
                        <Controller
                            control={form.control}
                            name="extra_info"
                            render={({field})=>(
                                <Textarea 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <SheetFooter>
                <div className='flex items-center gap-2 justify-end'>
                    <Button onClick={()=>{setOpen(false)}} variant='outline'>Annuleren</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!form.formState.isValid || createMutation.isLoading}
                    >
                        {createMutation.isLoading && <Icon.Loader2 className="w-4 h-4 animate-spin" />}
                        Opslaan
                    </Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>
  )
}

export default CreatePlayersOffered